import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Pause, RefreshCw, Mail, Eye, Settings, FileText, Ban, ShieldOff } from 'lucide-react';
import { emailService, EmailCampaign } from 'api/services/email.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  SCHEDULED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  RUNNING: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  // Open and waiting, not finished and not mid-send. Given its own colour because
  // reading it as PAUSED would send somebody looking for a stall that is not there.
  STANDING: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  PAUSED: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  COMPLETED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  CANCELLED: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};
const STATUS_FILTERS = ['all', 'DRAFT', 'SCHEDULED', 'RUNNING', 'STANDING', 'PAUSED', 'COMPLETED', 'CANCELLED', 'FAILED'];

const EmailCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [stats, setStats] = useState<any | null>(null);
  const [suppressed, setSuppressed] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    const [res, st, sup] = await Promise.all([
      emailService.listCampaigns(), emailService.getStats(), emailService.suppressionCounts(),
    ]);
    if (res.success) { setCampaigns(res.data || []); setError(null); }
    else setError(res.message || 'Failed to load campaigns');
    if (st.success) setStats(st.data || null);
    if (sup.success) setSuppressed(sup.data?.total ?? null);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Filtered client-side: the list endpoint returns an org's campaigns, which is
  // tens of rows, not thousands. The recipient list is the one that needed real
  // paging, and it has it.
  const visible = useMemo(() => campaigns.filter((c) => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.templateName || '').toLowerCase().includes(q);
  }), [campaigns, statusFilter, search]);

  const act = async (id: string, action: 'start' | 'pause' | 'cancel') => {
    if (action === 'cancel'
      && !window.confirm('Stop this campaign for good? Everything still pending will be closed out and cannot be resumed.')) return;
    setBusy(id);
    const res = action === 'start' ? await emailService.startCampaign(id)
      : action === 'pause' ? await emailService.pauseCampaign(id)
      : await emailService.cancelCampaign(id);
    if (!res.success) setError(res.message || `Failed to ${action}`);
    await load();
    setBusy(null);
  };

  /** Sent as a share of what was actually attempted — skipped rows were never tried. */
  const deliveryRate = (c: EmailCampaign) => {
    const attempted = (c.emailsSent || 0) + (c.emailsFailed || 0);
    return attempted > 0 ? Math.round(((c.emailsSent || 0) / attempted) * 100) : null;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Mail className="w-7 h-7 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Email Campaigns</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate('/email/suppressions')}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200">
            <ShieldOff className="w-4 h-4" /> Unsubscribes{suppressed != null ? ` (${suppressed})` : ''}
          </button>
          <button onClick={() => navigate('/email/templates')}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200">
            <FileText className="w-4 h-4" /> Templates
          </button>
          <button onClick={() => navigate('/email/settings')}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200">
            <Settings className="w-4 h-4" /> Sender
          </button>
          <button onClick={load} title="Refresh"
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/email/campaigns/new')}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Tile label="Campaigns" value={stats.campaigns ?? 0} tone="text-gray-700 dark:text-gray-200" />
          <Tile label="Recipients" value={stats.totalRecipients ?? 0} tone="text-gray-700 dark:text-gray-200" />
          <Tile label="Sent" value={stats.sent ?? 0} tone="text-green-600" />
          <Tile label="Failed" value={stats.failed ?? 0} tone="text-red-600" />
          <Tile label="Delivery rate" value={stats.deliveryRate != null ? `${stats.deliveryRate}%` : '—'}
            tone="text-indigo-600" sub="sent / attempted" />
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search campaigns"
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 min-w-[14rem]" />
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400">Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200">
            {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s === 'all' ? 'All' : s}</option>)}
          </select>
        </div>
        {(search || statusFilter !== 'all') && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {visible.length} of {campaigns.length}
          </span>
        )}
      </div>

      {error && <ErrorMessage message={error} />}

      {loading ? <LoadingSpinner /> : visible.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <Mail className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>{campaigns.length === 0
            ? 'No email campaigns yet. Create one to get started.'
            : 'No campaigns match this filter.'}</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Campaign</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Recipients</th>
                  <th className="px-4 py-3 font-medium">Sent / Failed / Skipped</th>
                  <th className="px-4 py-3 font-medium">Delivery</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {visible.map((c) => {
                  const rate = deliveryRate(c);
                  return (
                    <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{c.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{c.templateName}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[c.status || 'DRAFT']}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{c.totalRecipients ?? 0}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        <span className="text-green-600">{c.emailsSent ?? 0}</span> /
                        <span className="text-red-600"> {c.emailsFailed ?? 0}</span> /
                        <span className="text-orange-500"> {c.emailsSkipped ?? 0}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {rate == null ? <span className="text-gray-400">—</span> : `${rate}%`}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button title="Details" onClick={() => navigate(`/email/campaigns/${c.id}`)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg">
                            <Eye className="w-4 h-4" />
                          </button>
                          {(c.status === 'DRAFT' || c.status === 'PAUSED') && (
                            <button title="Start" disabled={busy === c.id} onClick={() => act(c.id!, 'start')}
                              className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg disabled:opacity-50">
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          {(c.status === 'RUNNING' || c.status === 'SCHEDULED') && (
                            <button title="Pause" disabled={busy === c.id} onClick={() => act(c.id!, 'pause')}
                              className="p-2 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg disabled:opacity-50">
                              <Pause className="w-4 h-4" />
                            </button>
                          )}
                          {['DRAFT', 'SCHEDULED', 'RUNNING', 'PAUSED'].includes(c.status || '') && (
                            <button title="Stop for good" disabled={busy === c.id} onClick={() => act(c.id!, 'cancel')}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg disabled:opacity-50">
                              <Ban className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const Tile: React.FC<{ label: string; value: number | string; tone: string; sub?: string }> =
  ({ label, value, tone, sub }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
    <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${tone}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

export default EmailCampaigns;
